<script setup lang="ts">
import { computed, ref } from 'vue';
import Popover from 'primevue/popover';
import { useTruncation } from '@peanuds/composables';
import {
  getTransactionDisplayReference,
  getTransactionReferenceDetails,
  type TransactionTableRow,
} from '@peanuds/shared';

type ReferenceDisplay = 'ellipsis' | 'preview-popover';
type ReferenceSource = 'transaction' | 'details';

const {
  row,
  display = 'ellipsis',
  source = 'transaction',
} = defineProps<{
  row: TransactionTableRow;
  display?: ReferenceDisplay;
  source?: ReferenceSource;
}>();

const { el: referenceEl, isTruncated } = useTruncation();
const popoverRef = ref<InstanceType<typeof Popover> | null>(null);

const referenceText = computed(() => {
  if (source === 'details') return getTransactionReferenceDetails(row) || '-';

  return getTransactionDisplayReference(row);
});

const previewText = computed(() => {
  const words = referenceText.value.trim().split(/\s+/);

  if (words.length <= 2) return referenceText.value;

  return `${words.slice(0, 2).join(' ')}…`;
});

const shouldUsePopoverPreview = computed(() => {
  return display === 'preview-popover' && previewText.value !== referenceText.value;
});

const showReferencePopover = (event: MouseEvent) => {
  if (!shouldUsePopoverPreview.value) return;
  popoverRef.value?.show(event);
};

const hideReferencePopover = () => {
  popoverRef.value?.hide();
};
</script>

<template>
  <span
    v-if="shouldUsePopoverPreview"
    class="cell-reference cell-reference--truncated"
    @mouseenter="showReferencePopover"
    @mouseleave="hideReferencePopover"
  >
    <span class="cell-reference__preview">{{ previewText }}</span>
    <Popover ref="popoverRef">
      <span class="cell-reference__popover-text">{{ referenceText }}</span>
    </Popover>
  </span>

  <span
    v-else
    ref="referenceEl"
    :class="{ 'cell-reference': true, 'cell-reference--truncated': isTruncated }"
    v-tooltip.top="
      isTruncated ? { value: referenceText, showDelay: 300, hideDelay: 150 } : undefined
    "
  >
    {{ referenceText }}
  </span>
</template>

<style scoped lang="scss">
.cell-reference {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;

  &--truncated {
    cursor: pointer;
  }

  &__preview {
    white-space: nowrap;
  }

  &__popover-text {
    display: block;
    max-width: 24rem;
    white-space: normal;
    word-break: break-word;
  }
}
</style>
